// The website's languages and the small helpers around them. Kept free of React and
// i18next so the API client, tests and plain helpers can use them.

export const LANGUAGES = [
  { code: "fr", label: "FR", name: "Français", locale: "fr-CA" },
  { code: "en", label: "EN", name: "English", locale: "en-CA" },
];

export const SUPPORTED = LANGUAGES.map((l) => l.code);
export const DEFAULT_LANGUAGE = "en";

// The person's own choice, saved when they press FR or EN.
export const STORAGE_KEY = "bolt.language";

// "fr", "fr-CA", "FR_ca" -> "fr"; anything we don't offer -> null.
export const normalizeLanguage = (value) => {
  const v = String(value || "")
    .trim()
    .toLowerCase();
  if (!v) return null;
  const base = v.split(/[-_]/)[0];
  return SUPPORTED.includes(base) ? base : null;
};

// The locale used for numbers, money and dates: fr-CA or en-CA.
export const localeOf = (code) =>
  (LANGUAGES.find((l) => l.code === normalizeLanguage(code)) || LANGUAGES[1])
    .locale;

// The choice is kept twice: in localStorage, and in a first-party cookie of the same name,
// because signing out runs localStorage.clear() and the choice should outlive that.
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // one year

const readCookie = () => {
  try {
    const match = document.cookie.match(/(?:^|;\s*)bolt\.language=([^;]*)/);
    return match ? decodeURIComponent(match[1]) : null;
  } catch (e) {
    return null;
  }
};

export const readSavedLanguage = () => {
  try {
    const saved = normalizeLanguage(window.localStorage.getItem(STORAGE_KEY));
    if (saved) return saved;
  } catch (e) {
    // private mode, blocked storage: try the cookie
  }
  return normalizeLanguage(readCookie());
};

// Fired on an explicit FR | EN choice; useLanguageSync saves it to the account.
export const LANGUAGE_CHOSEN_EVENT = "bolt:language-chosen";

// { announce: false } when the value comes from the account itself.
export const saveLanguage = (code, { announce = true } = {}) => {
  const lang = normalizeLanguage(code);
  if (!lang) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch (e) {
    // The choice still applies for this visit.
  }
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${STORAGE_KEY}=${lang}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
  } catch (e) {
    // cookies blocked
  }
  if (announce) {
    try {
      window.dispatchEvent(
        new CustomEvent(LANGUAGE_CHOSEN_EVENT, { detail: lang }),
      );
    } catch (e) {
      // very old browser: the device copy is enough
    }
  }
};
